import React, { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import "./ApprovalTeamMembersEditor.css";

function formatUserLabel(user) {
  if (!user) return "Unknown user";
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const parts = [name || "Unknown user", user.code, user.email].filter(Boolean);
  return parts.join(" | ");
}

// Shared member add/remove editor for ApprovalTeamDetail and ApprovalTeamForm.
// `memberUuids` is the source of truth; `initialMembers` (full objects from a
// GET /approval-team/{uuid}) fills in display data before the company's full
// user list has loaded, and for members no longer present in that list.
export default function ApprovalTeamMembersEditor({
  companyUuid,
  memberUuids = [],
  onChangeMemberUuids,
  initialMembers = [],
  readOnly = false,
}) {
  const [companyUsers, setCompanyUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesUnavailable, setRolesUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyUuid) {
      setCompanyUsers([]);
      setRoles([]);
      setRolesUnavailable(false);
      return;
    }
    setLoading(true);
    Promise.all([
      FieldConfigurationAPI.getUsers(companyUuid),
      FieldConfigurationAPI.getRoles(),
    ]).then(([usersResponse, rolesResponse]) => {
      setCompanyUsers(
        usersResponse.status === 200 && Array.isArray(usersResponse.data)
          ? usersResponse.data
          : []
      );
      // GET /role requires the "configure roles" permission (not "configure
      // approval groups"), so an approval-team-only admin can get a 403 here.
      // Don't let that empty out every user's option — fall back to
      // unfiltered rather than silently hiding everyone.
      if (rolesResponse.status === 200 && Array.isArray(rolesResponse.data)) {
        setRoles(rolesResponse.data);
        setRolesUnavailable(false);
      } else {
        setRoles([]);
        setRolesUnavailable(true);
      }
      setLoading(false);
    });
  }, [companyUuid]);

  // A user can only be added as an approver if their role grants the
  // "can_approve" timesheet permission.
  const rolesByUuid = useMemo(() => {
    const map = {};
    roles.forEach((r) => {
      if (r?.uuid) map[r.uuid] = r;
    });
    return map;
  }, [roles]);

  const canUserApprove = (user) =>
    rolesUnavailable ||
    rolesByUuid[user?.role_uuid]?.permissions?.timesheets?.can_approve === true;

  const userMap = useMemo(() => {
    const map = {};
    initialMembers.forEach((u) => {
      if (u?.uuid) map[u.uuid] = u;
    });
    companyUsers.forEach((u) => {
      if (u?.uuid) map[u.uuid] = u;
    });
    return map;
  }, [initialMembers, companyUsers]);

  const availableUsers = useMemo(
    () =>
      companyUsers.filter(
        (u) => !memberUuids.includes(u.uuid) && canUserApprove(u)
      ),
    [companyUsers, memberUuids, rolesByUuid, rolesUnavailable]
  );

  const handleRemove = (uuid) => {
    onChangeMemberUuids(memberUuids.filter((u) => u !== uuid));
  };

  const handleAdd = (e) => {
    const uuid = e.target.value;
    if (!uuid) return;
    onChangeMemberUuids([...memberUuids, uuid]);
    e.target.value = "";
  };

  return (
    <div className="role-detail__section">
      <h3 className="role-detail__section-title">
        Members ({memberUuids.length})
      </h3>

      {memberUuids.length === 0 ? (
        <p className="atm-editor__empty">No members added yet.</p>
      ) : (
        memberUuids.map((uuid) => (
          <div className="atm-editor__row" key={uuid}>
            <span className="atm-editor__row-label">
              {formatUserLabel(userMap[uuid])}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="atm-editor__row-remove"
                onClick={() => handleRemove(uuid)}
                aria-label="Remove member"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))
      )}

      {!readOnly && (
        <div>
          <label className="atm-editor__add-label" htmlFor="atm-add-member">
            Add Member
          </label>
          <select
            id="atm-add-member"
            className="atm-editor__add-select"
            value=""
            onChange={handleAdd}
            disabled={!companyUuid || loading}
          >
            <option value="" disabled>
              {loading ? "Loading users..." : "Select a user to add..."}
            </option>
            {availableUsers.map((u) => (
              <option key={u.uuid} value={u.uuid}>
                {formatUserLabel(u)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
